import { account, databases, functions, storage, teams, ID, Permission, Role, Query } from "./config"
import { sanitize, kebabToCamel, camelToKebab, extension, kebabToTitle } from "../utils"


// Master team ID whose members have access to all other teams
export const masterTeam = "takeoff-monkey"

// URL of the app
export const appUrl = "https://automatethat.app"



// USER FUNCTIONS
/**
 * @param jobs
 *      - list of in-process and completed jobs attached to user (populates downloads drawer)
 *      - each job has its data created/updated by the widget function
 * 
 * 
 * @Jobs
 * /- getJobs - get user's jobs
 * /- deleteJob - delete user's specific job; only accessible if the user is the same
 * 
 * @Storage (not in DB)
 * /- createUserAvatar - create a new user avatar; only accessible if the user is the same
 * 
 * @Other
 * /- isMaster - check if a user is part of master team
 * /- createUser - create a new user account; only accessible if the user is part of master team
 * /- getUsers - get all existing users
 * - deleteUser - delete user account; only accessible if the user is the same or part of master team
 */

/**
 * Check if a user is a root admin (part of master team).
 * 
 * @param {string} userID ID of the user to check for in master team. This is not the username, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<boolean>} Promise that resolves with the state of the passed user as a root admin.
 */
export async function isMaster(userID, raiseError) {
    try {
        const members = await teams.listMemberships(
            masterTeam,
            [],
            userID
        )
        return members.total > 0;
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("isMaster error:", error);
        return null;
    }
}

/**
 * Create a new user account.
 * 
 * @param {string} username Username for the new user. This is not the ID (will be sanitized by function).
 * @param {string} email User email.
 * @param {string} password User password.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
*/
export async function createUser(username, email, password, raiseError) {
    try {
        // Attempt to create a new user account first
        await account.create(sanitize(username), email, password, username).then(() => {
            // Create user's jobs collection
            functions.createExecution(
                "db-create-collection",
                JSON.stringify({
                    "databaseId": "jobs",
                    "collectionName": username,
                    "collectionId": sanitize(username),
                    "permissions": [
                        Permission.read(Role.any()),
                        Permission.update(Role.user(sanitize(username))),
                        Permission.update(Role.team(masterTeam)),
                        Permission.delete(Role.user(sanitize(username))),
                        Permission.delete(Role.team(masterTeam)),
                    ]
                }),
                false,
                '/',
                'GET'
            )

            // Create collection attributes for job metadata
            functions.createExecution(
                "db-create-attributes",
                JSON.stringify({
                    'databaseId': 'jobs',
                    'collectionId': sanitize(username),
                    'attributes': {
                        'is_new': {
                            'type': 'boolean'
                        },
                        'name': {
                            'type': 'string'
                        },
                        'files': {
                            'type': 'string',
                            'array': true
                        },
                        'start_time': {
                            'type': 'string'
                        },
                        'end_time': {
                            'type': 'string'
                        },
                        'progress': {
                            'type': 'integer',
                            'min': 0,
                            'max': 100
                        },
                        'status': {
                            'type': 'string'
                        },
                        'error_names': {
                            'type': 'string',
                            'array': true
                        },
                        'error_logs': {
                            'type': 'string',
                            'array': true
                        }
                    }
                }),
                false,
                '/',
                'GET'
            )

            // Create user's main storage bucket
            functions.createExecution(
                "storage-create-bucket",
                JSON.stringify({
                    "name": username,
                    "id": sanitize(username),
                    "permissions": [
                        Permission.read(Role.any()),
                        Permission.write(Role.user(sanitize(username))),
                        Permission.write(Role.team(masterTeam)),
                    ]
                }),
                false,
                '/',
                'GET'
            )

            // Create user's input storage bucket
            functions.createExecution(
                "storage-create-bucket",
                JSON.stringify({
                    "name": `${username}_input`,
                    "id": `${sanitize(username)}_input`,
                    "permissions": [
                        Permission.read(Role.user(sanitize(username))),
                        Permission.read(Role.team(masterTeam)),
                        Permission.write(Role.user(sanitize(username))),
                        Permission.write(Role.team(masterTeam)),
                    ]
                }),
                false,
                '/',
                'GET'
            )

            // Create user's output storage bucket
            functions.createExecution(
                "storage-create-bucket",
                JSON.stringify({
                    "name": `${username}_output`,
                    "id": `${sanitize(username)}_output`,
                    "permissions": [
                        Permission.read(Role.user(sanitize(username))),
                        Permission.read(Role.team(masterTeam)),
                        Permission.write(Role.user(sanitize(username))),
                        Permission.write(Role.team(masterTeam)),
                    ]
                }),
                false,
                '/',
                'GET'
            )
        }).then(() => {
            console.log("createUser complete.")
        })
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("createUser error:", error);
    }
}

/**
 * Get all existing users and their account data. Can optionally pass a username to get a specific user.
 * 
 * @param {string} username Optional. User to search for. This should not be the user ID, since this is for a direct username query.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Array>} Promise array that resolves when all user data is retrieved.
 */
export async function getUsers(username, raiseError) {
    try {
        const rawUserData = await functions.createExecution(
            "users-get-users",
            JSON.stringify({
                "query": username ? [ Query.equal('name', username) ] : null
            }),
            false,
            '/',
            'GET'
        )
        
        if (rawUserData && rawUserData.responseBody) {
            const userData = JSON.parse(rawUserData.responseBody).response.users
            return userData
        } else throw new Error("No user data retrieved. This is probably an issue with the get-users function.");
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("getUsers error:", error);
        return null;
    }
}

export async function deleteUser(userID, raiseError) {
    // TODO (something similar to deleteTeam)
}

/**
 * Get all download jobs for a user.
 * 
 * @param {string} userID ID of the user to check for in master team. This is not the username, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Array>} Promise object that resolves when all job data is retrieved.
 */
export async function getJobs(userID, raiseError) {
    try {
        return await databases.listDocuments(
            'jobs',
            userID,
            []
        );
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('getJobs error:', error);
        return null
    }
}

/**
 * Delete a specified job from a user's download jobs.
 * 
 * @param {string} userID ID of the user to check for in master team. This is not the username, since that may be changed.
 * @param {string} jobID ID of the job to delete.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function deleteJob(userID, jobID, raiseError) {
    try {
        return await databases.deleteDocument(
            'jobs',
            userID,
            jobID
        )
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('deleteJob error:', error);
    }
}

/**
 * Create a new user avatar, or update the existing one.
 * 
 * @param {string} userID User ID to check for in master team. This is not the username, since that may be changed.
 * @param {string} image Image File object to upload. This can also be a file path, which will be converted to a File object.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function createUserAvatar(userID, image, raiseError) {
    try {
        // Delete existing avatar
        try {
            if (await storage.getFile(userID, 'avatar')) {
                await storage.deleteFile(userID, 'avatar')
            }
        } catch (error) {}
        
        // Upload image either from file path or File object
        if (typeof image === 'string') {
            fetch(image)
                .then(response => response.blob())
                .then(blob => {
                    const file = new File([blob], 'avatar', { type: blob.type });
                    storage.createFile(
                        userID,
                        'avatar',
                        file,
                        ['read("any")']
                    )
                }).then(() => {
                    console.log("createUserAvatar complete.")
                })
        } else {
            await storage.createFile(
                userID,
                'avatar',
                image,
                ['read("any")']
            ).then(() => {
                console.log("createUserAvatar complete.")
            })
        }
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('createUserAvatar error:', error);
    }
}


// TEAM FUNCTIONS
/**
 * @param roles
 *      - Default roles:
 *          - Viewer: can view team data
 *          - Editor: can update team data (default)
 *          - Admin: can create/delete team data
 *          - Owner: full team control
 *      - Custom roles added to roles collection
 *          - roles can be assigned blanket permissions from:
 *              - create
 *              - read
 *              - update
 *              - delete
 *              - access (widgets)
 *          - specific resources can be tagged, and roles can access by tag
 *          - team roles updated w/ teams.updateMembershipRoles
 * @param boards (`board_{id}`)
 *      - board data (attributes = cols, documents = rows)
 *      - roles attribute listing roles that can access (default is any)
 *      - entire boards can be whitelisted/blacklisted w/ roles
 * @param widgets (`widget_{id}`)
 *      - widget_the-name -> userID: each widget setting
 *      - team-wide default settings for widget can be added later
 *      - widgets can be whitelisted w/ roles (default is any)
 * 
 * 
 * @Teams
 * /- getTeams - get a list of all existing teams; only accessible if the user is part of master team
 * /- createTeam - create a new team; only accessible if the user is part of master team
 * /- deleteTeam - delete a team; only accessible if the user is part of master team
 * 
 * @Users (not in DB)
 * /- getMember - get a specific user in the current team; only accessible if user is also part of team
 * /- listMembers - get all users in the current team, excluding hidden master-team members; only accessible if user is also part of team
 * 
 * @Roles
 * /- memberRoles - list member's roles; only accessible if the user is part of team
 * /- updateMemberRoles - update member's roles; only accessible if the user is an owner of the team
 * - teamRoles - list team's roles (both default and custom in roles collection); only accessible if user is part of team
 * - createTeamRole - create a new custom team role; only accessible if the user is an owner of the team
 * - updateTeamRole - update a custom team role permissions; only accessible if the user is an owner of the team
 * - deleteTeamRole - delete a custom team role; only accessible if the user is an owner of the team
 * 
 * @Boards (`board_{id}`)
 * /- createBoard - create a new board; only accessible if the user is an owner of the team
 *      - board-specific functions (TBD, this will be later)
 * /- getBoards - get all boards in the current team; only accessible if the user has permissions
 * /- deleteBoard - delete a board; only accessible if the user is an owner of the team
 * 
 * @Widgets (`widget_{id}`)
 * /- getWidgets - get all widgets in the current team
 * /- addWidget - add a widget to team access; only accessible if the user is an owner of the team
 * /- getWidgetData - get user's widget document data; only accessible if the user is part of team
 * /- updateWidgetData - update user's widget document data; only accessible if the user is part of team
 * /- removeWidget - remove a widget from team access; only accessible if the user is an owner of the team
 * 
 * @Storage (not in DB)
 * /- createTeamAvatar - create a new team avatar; only accessible if the user is an owner of the team
 */

/** @TEAMS */

/**
 * Create an invite for a user. Can be a generic invite to create a Takeoff Monkey Widgets account, or an invite to join a specific team.
 * 
 * @param {string} type Type of invite to create. Either 'join' or 'team'. If 'join', will invite a user to join an existing team (and create a new account if necessary); if 'team', will invite a user to setup a new team.
 * @param {string} email The email address of the user to invite.
 * @param {Array<string>} roles Optional. Roles to give the invited user. If type is 'team', will be ignored.
 * @param {string} teamID Optional. ID of the team to invite the user to. If type is 'team', will be ignored.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function createInvite(type, email, roles, teamID, raiseError) {
    try {
        const key = ID.unique();

        // Create invite document
        await databases.createDocument(
            type === 'join' ? teamID : masterTeam,
            'invites',
            key,
            {
                "email": email,
                "roles": type === 'join' ? roles : []
            }
        )

        // Send email
        if (type === 'join') {
            await functions.createExecution(
                "create-email",
                JSON.stringify({
                    to: email,
                    subject: "You're invited to " + kebabToTitle(teamID),
                    body: `<h3>You've been invited to join the ${kebabToTitle(teamID)} team on Takeoff Monkey Widgets!</h3><p>Login to your Takeoff Monkey Widgets team here: <a href='${appUrl}/welcome?team=${teamID}&key=${key}'>Sign in to ${kebabToTitle(teamID)}</a><br><br>Happy automating,<br><br>The Takeoff Monkey Widgets Team</p>`,
                })
            ).then((data) => {
                if (data.responseBody !== "Success") throw new Error(`Error sending email: ${data.errors}`);
            })
        } else if (type === 'team') {
            await functions.createExecution(
                "create-email",
                JSON.stringify({
                    to: email,
                    subject: "You're invited to create a team",
                    body: `<h3>You've been invited to create a team on Takeoff Monkey Widgets!</h3><p>Setup your team here: <a href='${appUrl}/new-team?key=${key}'>Create new team</a><br><br>Happy automating,<br><br>The Takeoff Monkey Widgets Team</p>`,
                })
            ).then((data) => {
                if (data.responseBody !== "Success") throw new Error(`Error sending email: ${data.errors}`);
            })
        } else if (type === 'reset') {
            await functions.createExecution(
                "user-reset-password",
                JSON.stringify({
                    email: email,
                    url: `${appUrl}/reset-password`
                })
            ).then((data) => {
                if (data.responseBody !== "Success") throw new Error(`Error sending email: ${data.errors}`);
            })
        }
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('getTeams error:', error);
    }
}

/**
 * Get a list of all existing teams.
 * 
 * @param {string} userID User ID to check for in master team. This is not the username, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Array>} Promise array of all existing teams.
 */
export async function getTeams(userID, raiseError) {
    try {
        if (!isMaster(userID)) throw new Error('User is not part of master team and does not have access to getTeams.');

        // List all databases, minus 'jobs'
        const getDB = await functions.createExecution(
            "db-list-databases",
            JSON.stringify({
                "query": [ Query.notEqual('name', 'jobs') ]
            }),
            false,
            '/',
            'GET'
        )

        // Return an array of database IDs only
        const databases = JSON.parse(getDB.responseBody).response.databases;
        return databases.map(database => database.$id);
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('getTeams error:', error);
    }
}

/**
 * Create a new team.
 * 
 * @param {string} team Name of the team. Function will sanitize this to create the team ID.
 * @param {string} userID User ID of the team owner. This is not the username, since that may be changed.
 * @param {string} username Username of the team owner.
 * @param {string} email Email of the team owner.
 * @param {string} website Optional. URL of the team's website.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function createTeam(team, key, website, raiseError) {
    try {
        // TODO: check whether the passed token exists as a valid invite, and return an error if it doesn't
        // await functions.createExecution(
        //     "db-get-document",
        //     JSON.stringify({
        //     'database_id': masterTeam,
        //     'collection_id': 'invites',
        //     'document_id': key
        //     }),
        //     false,
        //     '/',
        //     'GET'
        // ).then((data) => {
        //     const getInviteData = JSON.parse(data.responseBody).response
        //     if (userTeams?.includes(kebabToCamel(inviteData?.$databaseId))
        // })

        // Create team
        teams.create(
            sanitize(team),
            team,
            ['viewer', 'editor', 'admin', 'owner']
        )

        // Create team storage bucket
        functions.createExecution(
            "storage-create-bucket",
            JSON.stringify({
                "name": team,
                "id": sanitize(team),
                "permissions": [
                    Permission.read(Role.any()),
                    Permission.write(Role.team(sanitize(team))),
                    Permission.write(Role.team(masterTeam)),
                ]
            }),
            false,
            '/',
            'GET'
        )

        // Create team DB
        await functions.createExecution(
            "db-create-database",
            JSON.stringify({
                "name": team,
                "id": sanitize(team),
            }),
            false,
            '/',
            'GET'
        )

        // Create collection for storing any team metadata
        await functions.createExecution(
            "db-create-collection",
            JSON.stringify({
                "databaseId": sanitize(team),
                "collectionName": "Metadata",
                "collectionId": "metadata",
                "permissions": [
                    Permission.read(Role.any()),
                    Permission.write(Role.team(sanitize(team), "owner")),
                    Permission.write(Role.team(masterTeam)),
                ]
            }),
            false,
            '/',
            'GET'
        )

        // Create default collection attributes for team metadata
        await functions.createExecution(
            "db-create-attributes",
            JSON.stringify({
                'databaseId': sanitize(team),
                'collectionId': 'metadata',
                'attributes': {
                    'website': {
                        'type': 'url'
                    },
                }
            }),
            false,
            '/',
            'GET'
        )

        // Create team metadata document
        await databases.createDocument(
            sanitize(team),
            'metadata',
            'main',
            { 'website': website ? new URL(website) : null }
        )

        // Create invites collection for storing pending invites data
        await functions.createExecution(
            "db-create-collection",
            JSON.stringify({
                "databaseId": sanitize(team),
                "collectionName": "Member Invites",
                "collectionId": "invites",
                "permissions": [
                    Permission.read(Role.team(sanitize(team), "owner")),
                    Permission.write(Role.team(sanitize(team), "owner")),
                    Permission.read(Role.team(masterTeam)),
                    Permission.write(Role.team(masterTeam)),
                ]
            }),
            false,
            '/',
            'GET'
        )

        // Create attributes for invites
        await functions.createExecution(
            "db-create-attributes",
            JSON.stringify({
                'databaseId': sanitize(team),
                'collectionId': 'invites',
                'attributes': {
                    'email': {
                        'type': 'email'
                    },
                    'roles': {
                        'type': 'string',
                        'size': 64,
                        'array': true
                    },
                }
            }),
            false,
            '/',
            'GET'
        )

        // Create collection for storing custom role data
        await functions.createExecution(
            "db-create-collection",
            JSON.stringify({
                "databaseId": sanitize(team),
                "collectionName": "Custom Roles",
                "collectionId": "roles",
                "permissions": [
                    Permission.read(Role.any()),
                    Permission.write(Role.team(sanitize(team), "owner")),
                    Permission.write(Role.team(masterTeam)),
                ]
            }),
            false,
            '/',
            'GET'
        )

        // Create collection attributes for custom roles
        await functions.createExecution(
            "db-create-attributes",
            JSON.stringify({
                'databaseId': sanitize(team),
                'collectionId': 'roles',
                'attributes': {
                    // Role name (for user identification; can change later, unlike role ID)
                    'name': {
                        'type': 'string',
                    },
                    // Possible team permissions to assign to role (document)
                    'create': {
                        'type': 'boolean',
                    },
                    'read': {
                        'type': 'boolean',
                    },
                    'update': {
                        'type': 'boolean',
                    },
                    'delete': {
                        'type': 'boolean',
                    },
                    // For widget usage
                    'access': {
                        'type': 'boolean',
                    },
                }
            }),
            false,
            '/',
            'GET'
        )
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.warn(`createTeam error:`, error);
    }
}

/**
 * Delete an existing team.
 * 
 * @param {string} teamID ID of the team. This is not the team name, since that may be changed.
 * @param {string} userID User ID to check for, either in master team or as a team owner. This is not the username, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function deleteTeam(teamID, userID, raiseError) {
    try {
        // Check for either master team or team owner permissions
        let masterCheck = false

        await isMaster(userID)
            .then((isMaster) => {
                if (isMaster) masterCheck = true
            })

        await memberRoles(teamID, userID)
            .then((roles) => {
                if (!masterCheck && !roles.includes('owner')) throw new Error('User is not a team owner or part of master team and does not have access to deleteTeam.');
            })

        // Delete team
        await teams.delete(teamID)

        // Delete team DB
        await functions.createExecution(
            "db-delete-database",
            JSON.stringify({
                "id": teamID
            }),
            false,
            '/',
            'GET'
        )

        // Delete team storage bucket
        await functions.createExecution(
            "storage-delete-bucket",
            JSON.stringify({
                "id": teamID
            }),
            false,
            '/',
            'GET'
        ).then(() => {
            console.log("Finished deleting team.")
        })
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.warn(`deleteTeam error:`, error);
    }
}

/** @USERS */

/**
 * Get a specific user in the current team (since team account differs from personal account).
 * 
 * @param {string} teamID ID of the team. This is not the username, since that may be changed.
 * @param {string} userID User ID to get team member data for. This is not the username, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Object>} Promise object that resolves when member data is retrieved.
 */
export async function getMember(teamID, userID, raiseError) {
    try {
        const members = await teams.listMemberships(teamID);
        return members.memberships.find(m => m.userId === userID) || null;
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("getMember error:", error);
        return null;
    }
}

/**
 * List all members in the specified team.
 * 
 * @param {string} teamID ID of the team to list. This is not the username, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Array>} List of members in the team.
 */
export async function listMembers(teamID, raiseError) {
    try {
        const masterMembers = await teams.listMemberships(masterTeam);
        const members = (teamID === masterTeam) ?
            await teams.listMemberships(teamID) :
            await teams.listMemberships(teamID, [
                Query.notEqual('userId', masterMembers.memberships.map(m => m.userId))
            ]);
        
        return members.memberships;
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("listMembers error:", error);
        return null;
    }
}

/** @ROLES */

/**
 * Return a list of roles the user has in the current team.
 * 
 * @param {string} teamID ID of the team. This is not the username, since that may be changed.
 * @param {string} userID User ID to check for the role. This is not the username, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Array>} Promise object that resolves with an array of roles the user has in the team.
 */
export async function memberRoles(teamID, userID, raiseError) {
    try {
        const member = await getMember(teamID, userID)
        return member.roles
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("memberRoles error:", error)
        return null
    }
}

/**
 * Update the roles of a team member.
 * 
 * @param {string} teamID ID of the team. This is not the username, since that may be changed.
 * @param {string} userID User ID to update roles for. This is not the username, since that may be changed.
 * @param {Array<string>} roles Roles to update the user. This is the new complete list of roles the user will have.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function updateMemberRoles(teamID, userID, roles, raiseError) {
    try {
        // Check for either master team or team owner permissions
        let masterCheck = false

        await isMaster(userID)
            .then((isMaster) => {
                if (isMaster) masterCheck = true
            })

        await memberRoles(teamID, userID)
            .then((roles) => {
                if (!masterCheck && !roles.includes('owner')) throw new Error('User is not a team owner or part of master team and does not have access to deleteTeam.');
            })
        
        const getMemberID = await getMember(teamID, userID)

        await teams.updateMembership(teamID, getMemberID.$id, roles)
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("updateMemberRoles error:", error)
    }
}

/**
 * Return a list of custom roles in the team.
 * 
 * @param {string} teamID ID of the team. This is not the name, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Array>} Promise object that resolves with an array of all roles in the team.
 */
export async function teamRoles(teamID, raiseError) {
    try {
        const customRoles = await databases.listDocuments(
            teamID,
            'roles'
        )
        return customRoles
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("teamRoles error:", error)
        return null
    }
}

/** @BOARDS */

/**
 * Create a new board in the team DB. Permissions are set according to Appwrite permissions types:
 * https://appwrite.io/docs/advanced/platform/permissions
 * 
 * @param {string} teamID Team ID to create the board in.
 * @param {string} userID User ID to check for admin/owner permissions. This is not the username, since that may be changed.
 * @param {string} board Name for the new board. This is not the ID (will be sanitized by function).
 * @param {boolean} whitelisted Whether this board requires whitelisting of rows to specific roles/users. Default is `false`.
 * @param {Array<Permission>} permissions Permissions for the new board, based on Appwrite permissions.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function createBoard(teamID, userID, board, whitelisted, permissions, raiseError) {
    try {
        // Check for either master team or team owner permissions
        let masterCheck = false

        await isMaster(userID)
            .then((isMaster) => {
                if (isMaster) masterCheck = true
            })

        await memberRoles(teamID, userID)
            .then((roles) => {
                if (!masterCheck && !roles.includes('owner') && !roles.includes('admin') && !roles.includes('editor')) throw new Error('User does not have create permissions nor part of master team and does not have access to createBoard.');
            })

        await functions.createExecution(
            "db-create-collection",
            JSON.stringify({
                "databaseId": teamID,
                "collectionName": board,
                "collectionId": `board_${sanitize(board)}`,
                "permissions": permissions || [
                    Permission.read(Role.team(teamID, "viewer")),
                    Permission.read(Role.team(teamID, "editor")),
                    Permission.read(Role.team(teamID, "admin")),
                    Permission.read(Role.team(teamID, "owner")),
                    Permission.write(Role.team(teamID, "editor")),
                    Permission.write(Role.team(teamID, "admin")),
                    Permission.write(Role.team(teamID, "owner")),
                ],
                "documentSecurity": whitelisted || false
            }),
            false,
            '/',
            'GET'
        )
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("createBoard error:", error);
    }
}

/**
 * createTeam - create a new team; only accessible if the user is an admin
 * createBoard - create a new board in the team boards DB. this will also create a new document in `team-data` DB
 * createCol - create a new attribute in the board collection. type of UI element is specified, which needs to translate to a data type
 * createRow - create a new document in the board collection
 * editCol - edit attribute data in the board collection
 * editRow - edit a document's data in the board collection
 * deleteCol - delete an attribute from the board collection
 * deleteRow - delete a document from the board collection
 */
export function boardActions() {
    const team = {}
    // TODO: change this to dynamically pull from user's teams via API
    teamList.forEach((col) => {
        team[col] = {
            // this should set generic board attributes for when a new board collection is created
            createBoard: (payload, permissions, id) =>
                databases.createDocument(
                    col.dbId,
                    col.id,
                    id,
                    payload,
                    permissions
                ),
            
            // this should create a new attribute in the board
            createBoardAttribute: (payload, permissions, id) =>
                databases.createDocument(
                    col.dbId,
                    col.id,
                    id,
                    payload,
                    permissions
                ),
            
            update: (payload, permissions, id) =>
                databases.updateDocument(
                    col.dbId,
                    col.id,
                    id,
                    payload,
                    permissions
                ),
            
            delete: (id) => databases.deleteDocument(col.dbId, col.id, id),

            list: (queries = []) => databases.listDocuments(col.dbId, col.id, queries),

            get: (id) => databases.getDocument(col.dbId, col.id, id),
        }
    })
}

/**
 * Get all boards for a team that the user has read permissions for. Can optionally pass a board name to get a specific board.
 * 
 * @param {string} teamID Team ID to get boards for.
 * @param {string} userID User ID to check for read permissions. This is not the username, since that may be changed.
 * @param {string} board Name of a specific board to get. Will return any matches. If unspecified, defaults to all boards the user has read permissions for.
 * @param {Array<Query>} query Optional query to more granularly filter boards by.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Array>} Promise object that resolves when all boards are retrieved.
 */
export async function getBoards(teamID, userID, board, query, raiseError) {
    try {
        const getBoardData = await functions.createExecution(
            "db-list-collections",
            JSON.stringify({
                "databaseId": teamID,
                "collectionName": board ? `board_${sanitize(board)}` : "board_",
                "query": query || null
            }),
            false,
            '/',
            'GET'
        );

        const boardData = getBoardData;

        if (boardData && boardData.responseBody) {
            const collections = JSON.parse(boardData.responseBody).response.collections;
            const userRoles = await memberRoles(teamID, userID);

            // Check if user is a master team member (in which case they can see all boards)
            const masterMembers = await teams.listMemberships(masterTeam);
            const isMasterMember = masterMembers.memberships.some(member => member.userId === userID);

            if (isMasterMember) {
                console.log("User is a master team member. Returning all boards.")
                return collections;
            } else {
                // Filter collections based on user permissions
                // Since read is the lowest permission, we simply check for any of user's roles listed in collection's permissions
                const accessibleBoards = collections.filter(board => {
                    return board.$permissions.some(permission => {
                        return userRoles.some(role => permission.includes(role));
                    });
                });
                return accessibleBoards;
            }
        } else throw new Error("No response body received for getBoards. This is most likely an issue with the list-collections function.");
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('getBoards error:', error);
        return null;
    }
}

/**
 * Delete a board from the team database.
 * 
 * @param {string} teamID Team ID to delete board from. This is not the team name, since that may be changed.
 * @param {string} userID User ID to check for admin/owner permissions. This is not the username, since that may be changed.
 * @param {string} boardID Board ID to delete. This is not the board name, since that may be changed.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function deleteBoard(teamID, userID, boardID, raiseError) {
    try {
        // Check for either master team or team owner permissions
        let masterCheck = false

        await isMaster(userID)
            .then((isMaster) => {
                if (isMaster) masterCheck = true
            })

        await memberRoles(teamID, userID)
            .then((roles) => {
                if (!masterCheck && !roles.includes('owner') && !roles.includes('admin')) throw new Error('User is not a team admin/owner or part of master team and does not have access to deleteBoard.');
            })

        await functions.createExecution(
            "db-delete-collection",
            JSON.stringify({
                "databaseId": teamID,
                "collectionId": `board_${boardID}`
            }),
            false,
            '/',
            'GET'
        ).then(() => {
            console.log("Finished deleting board.")
        })
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('deleteBoard error:', error);
    }
}

/** @WIDGETS */

/**
 * Get all widgets in a team that the user has read permissions for. Can optionally pass a widget name to get a specific widget.
 * 
 * @param {string} teamID Team ID to get widgets for.
 * @param {string} userID User ID to check for read permissions. This is not the username, since that may be changed.
 * @param {string} widget Name of a specific widget to get. Will return any matches. If unspecified, defaults to all widgets the user has read permissions for.
 * @param {Array<Query>} query Optional query to more granularly filter widgets by.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<Array>} Promise object that resolves when all widgets are retrieved.
 */
export async function getWidgets(teamID, userID, widget, query, raiseError) {
    try {
        const getWidgetData = await functions.createExecution(
            "db-list-collections",
            JSON.stringify({
                "databaseId": teamID,
                "collectionName": widget ? `widget_${sanitize(widget)}` : "widget_",
                "query": query || null
            }),
            false,
            '/',
            'GET'
        );

        const widgetData = getWidgetData;

        if (widgetData && widgetData.responseBody) {
            const collections = JSON.parse(widgetData.responseBody).response.collections;
            const userRoles = await memberRoles(teamID, userID);

            // Check if user is a master team member (in which case they can see all widgets)
            const masterMembers = await teams.listMemberships(masterTeam);
            const isMasterMember = masterMembers.memberships.some(member => member.userId === userID);

            if (isMasterMember) {
                console.log("User is a master team member. Returning all widgets.")
                return collections;
            } else {
                // Filter collections based on user permissions
                // Since read is the lowest permission, we simply check for any of user's roles listed in collection's permissions
                const accessibleWidgets = collections.filter(widget => {
                    return widget.$permissions.some(permission => {
                        return userRoles.some(role => permission.includes(role));
                    });
                });
                return accessibleWidgets;
            }
        } else throw new Error("No response body received for getWidgets. This is most likely an issue with the list-collections function.");
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('getWidgets error:', error);
        return null;
    }
}

/**
 * Add a widget to team access. Permissions are set according to Appwrite permissions types:
 * https://appwrite.io/docs/advanced/platform/permissions
 * 
 * @param {string} teamID Team ID to add the widget to.
 * @param {string} widget Name for the new widget. This is not the ID (will be sanitized by function).
 * @param {string} attributes Attributes for the widget collection. These are the widget settings for each user.
 * @param {Array<Permission>} permissions Permissions for the new board, based on Appwrite permissions.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function addWidget(teamID, userID, widget, attributes, permissions, raiseError) {
    try {
        // Check for either master team or team owner permissions
        let masterCheck = false

        await isMaster(userID)
            .then((isMaster) => {
                if (isMaster) masterCheck = true
            })

        await memberRoles(teamID, userID)
            .then((roles) => {
                if (!masterCheck && !roles.includes('owner')) throw new Error('User is not an owner or part of master team and does not have access to addWidget.');
            })

        // Create widget collection in team DB
        await functions.createExecution(
            "db-create-collection",
            JSON.stringify({
                "databaseId": teamID,
                "collectionName": widget,
                "collectionId": `widget_${sanitize(widget)}`,
                "permissions": permissions || [
                    Permission.read(Role.team(teamID, "editor")),
                    Permission.read(Role.team(teamID, "admin")),
                    Permission.read(Role.team(teamID, "owner")),
                    Permission.write(Role.team(teamID, "editor")),
                    Permission.write(Role.team(teamID, "admin")),
                    Permission.write(Role.team(teamID, "owner")),
                ]
            }),
            false,
            '/',
            'GET'
        )

        // Create widget's attributes (widget settings for each user)
        await functions.createExecution(
            "db-create-attributes",
            JSON.stringify({
                'databaseId': teamID,
                'collectionId': `widget_${sanitize(widget)}`,
                'attributes': attributes
            }),
            false,
            '/',
            'GET'
        ).then(() => {
            console.log(`Successfully added widget ${widget}.`);
        })
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("addWidget error:", error);
    }
}

/**
 * Get a user's widget settings within team.
 * 
 * @param {string} teamID Team ID. This is not the name, since that may be changed.
 * @param {string} userID User ID to set widget settings for. This is not the username, since that may be changed.
 * @param {string} widget Name of the widget. This is not the ID (will be sanitized by function).
 * @param {Array<string>} search Array of specific key data to return. Can also be passed a single key as a string in non-array format. If unspecified, will return all widget data.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 * 
 * @returns {Promise<object>} Promise object that resolves when all widget data is retrieved.
 */
export async function getWidgetData(teamID, userID, widget, search, raiseError) {
    try {
        const widgetData = await databases.getDocument(
            teamID,
            `widget_${sanitize(widget)}`,
            userID
        );
        
        // If search is an array, return an object with the specified keys
        if (Array.isArray(search)) {
            return search.reduce((acc, key) => {
                if (widgetData[key] !== undefined) {
                    acc[key] = widgetData[key];
                }
                return acc;
            }, {});
        }
        
        // Fallback to returning specific key or all data
        return search ? widgetData[search] : widgetData;
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error("getWidgetData error:", error);
    }
}

/**
 * Update a user's widget settings within team.
 * 
 * @param {string} teamID Team ID. This is not the name, since that may be changed.
 * @param {string} userID User ID to set widget settings for. This is not the username, since that may be changed.
 * @param {string} widget Name of the widget. This is not the ID (will be sanitized by function).
 * @param {object} data Widget data to update. An object of key/value pairs for each widget setting.
 * @param {object} attributes Attributes to add to widget collection (if these are new data keys). An object of objects, containing at least a 'type' key.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function updateWidgetData(teamID, userID, widget, data, attributes, raiseError) {
    try {
        await databases.updateDocument(
            teamID,
            `widget_${sanitize(widget)}`,
            userID,
            data,
            ['read("any")']
        );
    } catch (err) {
        // Create a new widget data document, since it doesn't seem to exist yet
        try {
            console.warn(`${widget} widget data does not exist for ${JSON.stringify(attributes)}. Creating new...`);
            
            await functions.createExecution(
                "db-create-attributes",
                JSON.stringify({
                    'databaseId': teamID,
                    'collectionId': `widget_${sanitize(widget)}`,
                    'attributes': attributes
                })
            );

            // Try to update again (if this is a case of adding new attributes to an existing document)
            try {
                await databases.updateDocument(
                    teamID,
                    `widget_${sanitize(widget)}`,
                    userID,
                    data,
                    ['read("any")']
                ).then(() => {
                    console.log(`Successfully updated ${widget} widget data with ${JSON.stringify(attributes)}.`);
                })
            } catch (error) {
                await databases.createDocument(
                    teamID,
                    `widget_${sanitize(widget)}`,
                    userID,
                    data,
                    ['read("any")']
                ).then(() => {
                    console.log(`Successfully created ${widget} widget data with ${JSON.stringify(attributes)}.`);
                })
            }
        } catch (error) {
            if (raiseError) throw new Error(error);
            else console.error("updateWidgetData error:", error);
        }
    }
}

/**
 * Remove a widget from the team database.
 * 
 * @param {string} teamID Team ID to remove widget from. This is not the team name, since that may be changed.
 * @param {string} userID User ID to check for admin/owner permissions. This is not the username, since that may be changed.
 * @param {string} widget Name for the new widget. This is not the ID (will be sanitized by function).
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function removeWidget(teamID, userID, widget, raiseError) {
    try {
        // Check for either master team or team owner permissions
        let masterCheck = false

        await isMaster(userID)
            .then((isMaster) => {
                if (isMaster) masterCheck = true
            })

        await memberRoles(teamID, userID)
            .then((roles) => {
                if (!masterCheck && !roles.includes('owner')) throw new Error('User is not an owner or part of master team and does not have access to removeWidget.');
            })

        await functions.createExecution(
            "db-delete-collection",
            JSON.stringify({
                "databaseId": teamID,
                "collectionId": `widget_${sanitize(widget)}`
            }),
            false,
            '/',
            'GET'
        ).then(() => {
            console.log("Finished removing widget.")
        })
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('removeWidget error:', error);
    }
}

/** @STORAGE */

/**
 * Create a new team avatar, or update the existing one.
 * 
 * @param {string} teamID Team ID to create avatar for. This is not the team name, since that may be changed.
 * @param {string} userID User ID to check for permissions. This is not the username, since that may be changed.
 * @param {string} image Image File object to upload. This can also be a file path, which will be converted to a File object.
 * @param {boolean} raiseError Optional. If `true`, will throw errors instead of logging them. Default is `false`.
 */
export async function createTeamAvatar(teamID, userID, image, raiseError) {
    try {
        // Check for either master team or team owner permissions
        let masterCheck = false

        await isMaster(userID)
            .then((isMaster) => {
                if (isMaster) masterCheck = true
            })

        await memberRoles(teamID, userID)
            .then((roles) => {
                if (!masterCheck && !roles.includes('owner') && !roles.includes('admin')) throw new Error('User does not have administrative permissions nor part of master team and does not have access to createTeamAvatar.');
            })

        // Delete existing avatar
        try {
            if (await storage.getFile(teamID, 'avatar')) {
                await storage.deleteFile(teamID, 'avatar')
            }
        } catch (error) {}
        
        // Upload image either from file path or File object
        if (typeof image === 'string') {
            fetch(image)
                .then(response => response.blob())
                .then(blob => {
                    const file = new File([blob], 'avatar', { type: blob.type });
                    storage.createFile(
                        teamID,
                        'avatar',
                        file,
                        ['read("any")']
                    )
                }).then(() => {
                    console.log("createTeamAvatar complete.")
                })
        } else {
            await storage.createFile(
                teamID,
                'avatar',
                image,
                ['read("any")']
            ).then(() => {
                console.log("createTeamAvatar complete.")
            })
        }
    } catch (error) {
        if (raiseError) throw new Error(error);
        else console.error('createTeamAvatar error:', error);
    }
}